import React, { useState } from 'react';
import {
    ShieldCheck,
    ArrowLeft, Zap, CheckCircle2, XCircle,
    User, History, Play
} from 'lucide-react';
import { Badge } from './ui/badge';

export default function WorkspaceHealthDetail({ workspaceId, health, intervention, onIntervention, onClose }) {
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('ACTIONED');

    if (!health) return null;

    const getBandColor = (band) => {
        switch (band) {
            case 'HEALTHY': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
            case 'WATCH': return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
            case 'AT_RISK': return 'text-rose-400 bg-rose-500/10 border-rose-500/20';
            default: return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
        }
    };

    const handleSubmit = () => {
        if (!intervention?.recommended) return;
        onIntervention({
            playbookType: intervention.recommended.type,
            status,
            notes
        });
        setNotes('');
    };

    return (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-[#0d0d0f] border-l border-slate-800 shadow-2xl z-50 flex flex-col transform transition-transform duration-300 overflow-hidden">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-[#0a0a0c]">
                <div className="flex items-center gap-3">
                    <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-md transition-colors text-slate-500">
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h2 className="text-sm font-black text-white uppercase tracking-tight">Workspace Success Drill</h2>
                        <p className="text-[10px] text-slate-500 font-mono">{workspaceId}</p>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                {/* Health Band & Score */}
                <div className="text-center space-y-4">
                    <div className="inline-flex items-center justify-center p-6 rounded-full bg-slate-900 border border-slate-800 relative">
                        <div className="text-4xl font-black text-white">{health.score}</div>
                        <div className="absolute -bottom-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-lg bg-slate-950 border-slate-800">
                            Score
                        </div>
                    </div>
                    
                    <div>
                        <Badge className={`${getBandColor(health.band)} px-4 py-1 text-xs font-black tracking-[0.2em] border shadow-sm`}>
                            {health.band}
                        </Badge>
                    </div>
                </div>

                {/* Recommended Playbook */}
                {intervention?.recommended && (
                    <div className="p-5 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl space-y-4">
                        <div className="flex items-center gap-2 text-indigo-400">
                            <Zap className="w-4 h-4 fill-current" />
                            <span className="text-xs font-black uppercase tracking-widest">Recommended Playbook</span>
                        </div>
                        
                        <div>
                            <div className="text-sm font-bold text-white mb-1">{intervention.recommended.name}</div>
                            <p className="text-[11px] text-slate-400 leading-relaxed">{intervention.recommended.description}</p>
                        </div>

                        <div className="space-y-2">
                            {intervention.recommended.actions.map((action, i) => (
                                <div key={i} className="flex items-start gap-2 text-[10px] text-indigo-300/80">
                                    <Play className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>{action}</span>
                                </div>
                            ))}
                        </div>

                        {/* Intervention Action Controls */}
                        <div className="pt-4 border-t border-indigo-500/10 space-y-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Operator Status</label>
                                <select 
                                    value={status} 
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-xs text-slate-300 focus:outline-none focus:border-indigo-500/50"
                                >
                                    <option value="REVIEWED">REVIEWED</option>
                                    <option value="ACTIONED">ACTIONED</option>
                                    <option value="COMMERCIAL_BRIDGE">COMMERCIAL BRIDGE (Sales Handoff)</option>
                                    <option value="FOLLOW_UP_PENDING">FOLLOW UP PENDING</option>
                                    <option value="RESOLVED">RESOLVED</option>
                                    <option value="DISMISSED">DISMISSED</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Operator Notes</label>
                                <textarea 
                                    placeholder="Add a private note about this intervention..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-slate-300 min-h-[80px] focus:outline-none focus:border-indigo-500/50"
                                />
                            </div>
                            <button 
                                onClick={handleSubmit}
                                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-black py-2.5 rounded-lg transition-colors text-xs uppercase tracking-widest"
                            >
                                Log Intervention
                            </button>
                        </div>
                    </div>
                )}

                {/* Intervention History */}
                {intervention?.history?.length > 0 && (
                    <div className="space-y-4">
                        <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                            <History className="w-4 h-4" />
                            Success Timeline
                        </h3>
                        <div className="space-y-3">
                            {intervention.history.map((h, i) => (
                                <div key={i} className="p-4 bg-slate-900/30 border border-slate-800 rounded-xl space-y-2">
                                    <div className="flex justify-between items-center">
                                        <Badge className="bg-slate-800 text-[9px] font-black">{h.status}</Badge>
                                        <span className="text-[9px] text-slate-600 font-mono">{new Date(h.timestamp).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                        <User className="w-3 h-3" />
                                        {h.operatorId?.name || 'Operator'}
                                    </div>
                                    {h.notes && (
                                        <p className="text-[11px] text-slate-500 bg-slate-950/50 p-2 rounded italic border border-slate-800/50">
                                            "{h.notes}"
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Health Reason Logic */}
                <div className="space-y-4">
                    <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4" />
                        Health Rationale
                    </h3>
                    <div className="space-y-2">
                        {health.reasons.map((reason, idx) => (
                            <div key={idx} className="p-3 bg-slate-900/10 border border-slate-800/30 rounded-xl flex items-center gap-3">
                                {reason.toLowerCase().includes('no') || reason.toLowerCase().includes('low') || reason.toLowerCase().includes('friction') ? (
                                    <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                                ) : (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                )}
                                <span className="text-xs font-medium text-slate-400">{reason}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="p-6 bg-[#0a0a0c] border-t border-slate-800">
                <p className="text-[9px] text-slate-600 font-medium uppercase tracking-widest text-center">Audit-Logged Intervention Surface</p>
            </div>
        </div>
    );
}
